import { Eye, FileText, Plus, RefreshCw, Upload } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { purchasesService, PurchaseRecord } from '../../services/purchases.service'
import { supplierService, SupplierItem } from '../../services/supplier.service'
import { useSessionStore } from '../../store/auth/auth.store'
import { SearchFilter } from '../ui/SearchFilter'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { DiscreteLoader } from '../ui/discrete-loader'
import { GlobalPaginator } from '../ui/global-paginator'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { TabsContent } from '../ui/tabs'
import { GlobalFilterBar } from '../ui/GlobalFilterBar'

import { DetailModal } from './modals/DetailModal'
import { usePurchaseBranches } from './use-purchase-branches'

interface ProductEntriesTabProps {
  refreshKey: number
  openXmlEntry: () => void
  openManualEntry: () => void
}

const perPage = 10

const money = (value: string | null, currency = 'MXN') => value === null
  ? 'Sin capturar'
  : new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(value))

export function ProductEntriesTab({ refreshKey, openXmlEntry, openManualEntry }: ProductEntriesTabProps) {
  const canAction = useSessionStore((state) => state.canAction)
  const canCreate = canAction('purchases.purchase.create')
  const [entries, setEntries] = useState<PurchaseRecord[]>([])
  const [suppliers, setSuppliers] = useState<SupplierItem[]>([])
  const [search, setSearch] = useState('')
  const [supplierId, setSupplierId] = useState('all')
  const [sucursalId, setSucursalId] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [retryKey, setRetryKey] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pagination, setPagination] = useState({ total: 0, from: 0, to: 0 })
  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<PurchaseRecord | null>(null)
  const branchesQuery = usePurchaseBranches(true)

  useEffect(() => {
    let mounted = true
    supplierService.getItems({ all: true }).then((response) => {
      if (!mounted) return
      const data = Array.isArray(response.data) ? response.data : response.data.data
      setSuppliers(data)
    }).catch(() => {
      if (mounted) setSuppliers([])
    })
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError(false)

    purchasesService.getProductEntries({
      search: search || undefined,
      supplier_id: supplierId === 'all' ? undefined : Number(supplierId),
      sucursal_id: sucursalId === 'all' ? undefined : Number(sucursalId),
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page,
      per_page: perPage,
    }).then((response) => {
      if (!mounted) return
      setEntries(response.data.data)
      setPagination({
        total: response.data.total,
        from: response.data.from ?? 0,
        to: response.data.to ?? 0,
      })
    }).catch(() => {
      if (!mounted) return
      setEntries([])
      setPagination({ total: 0, from: 0, to: 0 })
      setError(true)
    }).finally(() => {
      if (mounted) setLoading(false)
    })

    return () => { mounted = false }
  }, [dateFrom, dateTo, page, refreshKey, retryKey, search, supplierId, sucursalId])

  const supplierOptions = useMemo(
    () => [...suppliers].sort((a, b) => a.name.localeCompare(b.name)),
    [suppliers],
  )
  const branches = branchesQuery.data ?? []

  const openDetail = async (entry: PurchaseRecord) => {
    setDetailOpen(true)
    setDetailLoading(true)
    setSelectedEntry(entry)
    try {
      const response = await purchasesService.getProductEntry(entry.id)
      setSelectedEntry(response.data)
    } catch {
      setDetailOpen(false)
      setSelectedEntry(null)
      toast.error('No fue posible cargar el detalle del ingreso')
    } finally {
      setDetailLoading(false)
    }
  }

  return (
    <TabsContent value="products" className="space-y-4">
      <div className="space-y-3">
  <p className="text-sm text-header-foreground">
    {pagination.total} ingresos registrados
  </p>

     <GlobalFilterBar
  show={[]}
  values={{}}
  onChange={() => {}}
>
  <div className="col-span-full flex w-full flex-col gap-4">
    {/* Primera fila */}
   
    <div className="flex w-full flex-wrap items-center gap-10">
      <SearchFilter
        className="w-full sm:w-64"
        placeholder="Nota, producto o proveedor..."
        onSearch={(value) => {
          setSearch(value)
          setPage(1)
        }}
      />

      <div className="space-y-1">
        <Select
          value={sucursalId}
          onValueChange={(value) => {
            setSucursalId(value)
            setPage(1)
          }}
          disabled={branchesQuery.isLoading || branchesQuery.isError}
        >
          <SelectTrigger className="w-full sm:w-52">
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="all">
              {branchesQuery.isLoading
                ? 'Cargando sucursales...'
                : 'Todas las sucursales'}
            </SelectItem>

            {branches.map((branch) => (
              <SelectItem
                key={branch.id}
                value={String(branch.id)}
              >
                {branch.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {branchesQuery.isError && (
          <p className="text-xs text-red-500">
            No fue posible cargar las sucursales.
          </p>
        )}
      </div>

      <Select
        value={supplierId}
        onValueChange={(value) => {
          setSupplierId(value)
          setPage(1)
        }}
      >
        <SelectTrigger className="w-full sm:w-52">
          <SelectValue placeholder="Proveedor" />
        </SelectTrigger>

        <SelectContent>
          <SelectItem value="all">
            Todos los proveedores
          </SelectItem>

          {supplierOptions.map((supplier) => (
            <SelectItem
              key={supplier.id}
              value={String(supplier.id)}
            >
              {supplier.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    {/* Segunda fila */}
    <div className="flex w-full flex-wrap items-center gap-6">
      <div className="flex items-center gap-2">
        <label className="whitespace-nowrap text-sm font-medium text-card-foreground">
          Fecha Inicio
        </label>

        <Input
          type="date"
          value={dateFrom}
          onChange={(event) => {
            setDateFrom(event.target.value)
            setPage(1)
          }}
          className="h-10 w-[180px]"
          aria-label="Fecha inicial"
        />
      </div>

      <div className="flex items-center gap-2">
        <label className="whitespace-nowrap text-sm font-medium text-card-foreground">
          Fecha Fin
        </label>

        <Input
          type="date"
          value={dateTo}
          onChange={(event) => {
            setDateTo(event.target.value)
            setPage(1)
          }}
          className="h-10 w-[180px]"
          aria-label="Fecha final"
        />
      </div>

      {canCreate && (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={openXmlEntry}
          >
            <Upload className="mr-2 h-4 w-4" />
            Cargar XML de Factura
          </Button>

          <Button
            type="button"
            onClick={openManualEntry}
          >
            <Plus className="mr-2 h-4 w-4" />
            Agregar sin XML
          </Button>
        </>
      )}
    </div>
  </div>
</GlobalFilterBar>
      </div>

      {loading ? (
        <div className="min-h-64 rounded-lg border border-table-border bg-table-bg"><DiscreteLoader text="Cargando productos ingresados..." /></div>
      ) : error ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="font-medium text-red-800">No fue posible cargar los ingresos</p>
          <Button variant="outline" className="mt-4" onClick={() => setRetryKey((value) => value + 1)}>
            <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
          </Button>
        </div>
      ) : entries.length === 0 ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-table-border bg-table-bg p-6 text-center">
          <FileText className="mb-3 h-10 w-10 text-card-description" />
          <p className="font-medium text-table-foreground">No se encontraron ingresos</p>
          <p className="mt-1 text-sm text-card-description">Ajusta los filtros o registra la primera nota manual.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-table-border bg-table-bg">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1160px]">
              <thead className="border-b border-table-border bg-table-header-bg text-left text-xs font-medium uppercase text-table-header-foreground">
                <tr>
                  <th className="px-4 py-3">Folio</th>
                  <th className="px-4 py-3">Origen</th>
                  <th className="px-4 py-3">Sucursal</th>
                  <th className="px-4 py-3">Proveedor</th>
                  <th className="px-4 py-3">Registrado por</th>
                  <th className="px-4 py-3">Fecha compra</th>
                  <th className="px-4 py-3">Fecha ingreso</th>
                  <th className="px-4 py-3">Forma de pago</th>
                  <th className="px-4 py-3">Productos</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-table-border">
                {entries.map((entry) => (
                  <tr key={entry.id} className="text-table-foreground transition-colors hover:bg-table-row-hover">
                    <td className="px-4 py-4 font-medium">{entry.reference_number}</td>
                    <td className="px-4 py-4"><Badge variant="outline" className="border-border text-table-foreground">{entry.source === 'cfdi_xml' ? 'CFDI XML' : entry.source === 'excel' ? 'Excel' : 'Manual / Sin XML'}</Badge></td>
                    <td className="px-4 py-4">{entry.sucursal.name}</td>
                    <td className="px-4 py-4">{entry.supplier.name}</td>
                    <td className="px-4 py-4">{entry.created_by?.full_name ||`${entry.created_by?.name ?? ''} ${entry.created_by?.last_name ?? ''}`.trim() ||'Sin registrar'}</td>
                    <td className="px-4 py-4">{entry.purchase_date}</td>
                    <td className="px-4 py-4">{entry.entry_date}</td>
                    <td className="px-4 py-4">{entry.payment_condition === 'cash' ? 'Contado' : 'Crédito'}</td>
                    <td className="px-4 py-4">{entry.items_count}</td>
                    <td className="px-4 py-4 font-semibold">{money(entry.total_amount, entry.currency)}</td>
                    <td className="px-4 py-4"><Badge className="bg-blue-100 text-blue-700">Procesado</Badge></td>
                    <td className="px-4 py-4">
                      <button type="button" onClick={() => void openDetail(entry)} className="rounded p-1 " title="Ver detalle">
                        <Eye className="h-4 w-4 text-table-foreground transition-colors group-hover:text-primary" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <GlobalPaginator
            totalItems={pagination.total}
            itemsPerPage={perPage}
            currentPage={page}
            from={pagination.from}
            to={pagination.to}
            onPageChange={setPage}
          />
        </div>
      )}

      <DetailModal
        showDetailModal={detailOpen}
        setShowDetailModal={setDetailOpen}
        selectedEntry={selectedEntry}
        isLoading={detailLoading}
      />
    </TabsContent>
  )
}
